import React from "react";
import { useDispatch, useSelector } from "react-redux";
import AppContainer from "../AppContainer";
import Button from "../Button";
import { logout } from "../../store/user/actions";
import * as styles from "./index.module.scss";

const UserIcon = ({ accentColor = "#4CAF50" }) => (
    <svg 
        className={styles.UserIcon} 
        viewBox="0 0 120 120" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
    >
        <defs>
            <linearGradient id="userCircleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={accentColor} stopOpacity="0.15" />
                <stop offset="100%" stopColor={accentColor} stopOpacity="0.05" />
            </linearGradient>
        </defs>
        
        {/* Outer decorative ring */}
        <circle 
            cx="60" 
            cy="60" 
            r="58" 
            stroke={accentColor} 
            strokeWidth="1" 
            strokeOpacity="0.2" 
            fill="none"
        />
        
        {/* Main background circle */}
        <circle 
            cx="60" 
            cy="60" 
            r="50" 
            fill="url(#userCircleGradient)"
        />
        
        {/* Inner circle border */}
        <circle 
            cx="60" 
            cy="60" 
            r="50" 
            stroke={accentColor} 
            strokeWidth="3" 
            fill="none"
            strokeOpacity="0.4"
        />
        
        {/* User head */}
        <circle 
            cx="60" 
            cy="45" 
            r="18" 
            fill={accentColor}
            fillOpacity="0.8"
        />
        
        {/* User body */}
        <path 
            d="M30 95 C30 72 45 62 60 62 C75 62 90 72 90 95" 
            fill={accentColor}
            fillOpacity="0.8"
        />
    </svg>
);

const AlreadyLoggedInView = ({ T, appStyles, redirectUrl }) => {
    const dispatch = useDispatch();
    const user = useSelector(({ user }) => user);

    const accentColor = appStyles?.actionColor || "#4CAF50";
    const textColor = appStyles?.cardText || appStyles?.textColor || "#333";

    const userName = user?.userDetails?.data?.name || user?.signupState?.name || "";
    const userEmail = user?.userDetails?.data?.email || "";

    const handleLogout = () => {
        dispatch(logout());
        // Notify parent window about logout
        window?.parent.postMessage("IFRAME:USER-LOGGED-OUT", "*");
    };

    // Use redirectUrl if provided, otherwise default to root
    const buttonUrl = redirectUrl || "/";

    const handleContinue = () => {
        console.log('AlreadyLoggedInView: Continuing to', buttonUrl);
        console.log('AlreadyLoggedInView: redirectUrl=', redirectUrl);

        // Validate URL before redirecting
        const isValidUrl = (url) => {
            if (!url) return false;
            try {
                new URL(url);
                return true;
            } catch {
                // Check if it's a relative path
                return url.startsWith('/');
            }
        };

        const targetUrl = redirectUrl || buttonUrl;

        if (!isValidUrl(targetUrl)) {
            console.error('AlreadyLoggedInView: Invalid URL', targetUrl);
            // Fallback to root
            if (window.self !== window.top) {
                window.top.location.href = '/';
            } else {
                window.location.href = '/';
            }
            return;
        }

        // Redirect the whole page (parent window if in iframe)
        console.log('AlreadyLoggedInView: Redirecting to:', targetUrl);
        if (window.self !== window.top) {
            // In iframe - redirect parent window
            try {
                window.top.location.href = targetUrl;
            } catch (e) {
                // Cross-origin restriction - fallback to iframe redirect
                console.log('AlreadyLoggedInView: Cross-origin, redirecting iframe');
                window.location.href = targetUrl;
            }
        } else {
            // Not in iframe - redirect normally
            window.location.href = targetUrl;
        }
    };

    return (
        <AppContainer.Content
            appStyles={appStyles}
            animate
        >
            <div className={styles.Container}>
                <div className={styles.IconWrapper}>
                    <UserIcon accentColor={accentColor} />
                </div>

                <h2 
                    className={styles.Title} 
                    style={{ 
                        color: textColor,
                        ...appStyles?.SignupTitle 
                    }}
                >
                    {T ? T("You're already signed in!") : "You're already signed in!"}
                </h2>
                
                {userName && (
                    <p 
                        className={styles.UserName}
                        style={{ color: accentColor }}
                    >
                        {userName}
                    </p>
                )}
                
                {userEmail && (
                    <p 
                        className={styles.UserEmail}
                        style={{ color: textColor }}
                    >
                        {userEmail}
                    </p>
                )}
                
                <p 
                    className={styles.Subtitle}
                    style={{ 
                        color: textColor,
                        ...appStyles?.SignupGreeting 
                    }}
                >
                    {T 
                        ? T("You're already a member. Enjoy your rewards and exclusive offers!") 
                        : "You're already a member. Enjoy your rewards and exclusive offers!"
                    }
                </p>

                <div className={styles.ButtonWrapper}>
                    <Button
                        onClick={handleContinue}
                        appStyles={appStyles}
                        centered
                        className={styles.Button}
                    >
                        {redirectUrl
                            ? (T ? T("Continue") : "Continue")
                            : (T ? T("Start ordering") : "Start ordering")
                        }
                    </Button>
                </div>

                <div className={styles.LogoutWrapper}>
                    <button 
                        onClick={handleLogout}
                        className={styles.LogoutButton}
                        style={{ color: accentColor }}
                    >
                        {T ? T("Not you? Sign out") : "Not you? Sign out"}
                    </button>
                </div>
            </div>
        </AppContainer.Content>
    );
};

export default AlreadyLoggedInView;
